import React, { useState } from "react";
import {
  Box,
  Container,
  Heading,
  Text,
  VStack,
  Button,
  SimpleGrid,
  Flex,
  Icon,
  Badge,
  Card,
  CardHeader,
  CardBody,
  useColorMode,
  Avatar,
  Input,
  InputGroup,
  InputLeftElement,
  Select,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  Divider,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
  FormControl,
  FormLabel,
  Textarea,
} from "@chakra-ui/react";
import {
  CheckIcon,
  ChevronRightIcon,
  SearchIcon,
} from "@chakra-ui/icons";
import {
  FaStore,
  FaCoins,
  FaChartLine,
  FaUsers,
  FaFire,
  FaShoppingCart,
  FaTrophy,
  FaRocket,
  FaBuilding,
  FaGlobe,
} from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { NavbarWithCallToAction } from "../../components/chakra/NavbarWithCallToAction/NavbarWithCallToAction";
import { FooterWithFourColumns } from "../../components/chakra/FooterWithFourColumns/FooterWithFourColumns";
import { getColor, getBrandValue } from "../../brandConfig";

const Marketplace = () => {
  const navigate = useNavigate();
  const { colorMode } = useColorMode();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [selectedListing, setSelectedListing] = useState<any>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterCategory, setFilterCategory] = useState("all");

  const bg = getColor("background.main", colorMode);
  const cardGradientBg =
    colorMode === "light"
      ? `linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(248, 248, 248, 0.9) 50%, rgba(255, 255, 255, 0.95) 100%)`
      : `linear-gradient(135deg, rgba(20, 20, 20, 0.8) 0%, rgba(30, 30, 30, 0.6) 50%, rgba(20, 20, 20, 0.8) 100%)`;
  const cardBorder =
    colorMode === "light" ? "rgba(0, 0, 0, 0.1)" : "rgba(255, 255, 255, 0.1)";
  const textPrimary = getColor(
    colorMode === "light" ? "text.primary" : "text.primaryDark",
    colorMode
  );
  const textSecondary = getColor(
    colorMode === "light" ? "text.secondary" : "text.secondaryDark",
    colorMode
  );
  const textMuted = getColor(
    colorMode === "light" ? "text.muted" : "text.mutedDark",
    colorMode
  );

  // Mock marketplace listings
  const marketplaceListings = [
    {
      id: 1,
      businessName: "CloudTech Solutions",
      tokenSymbol: "CTS",
      description: "SaaS platform for cloud infrastructure management with 500+ enterprise clients",
      category: "Technology",
      monthlyRevenue: 85000,
      monthlyProfit: 35000,
      tokensForSale: 50000,
      totalSupply: 1000000,
      pricePerToken: 2.5,
      tithingPercentage: 20,
      logo: "https://via.placeholder.com/100/3B82F6/FFFFFF?text=CTS",
      verified: true,
      featured: true,
    },
    {
      id: 2,
      businessName: "GreenLeaf Consulting",
      tokenSymbol: "GLC",
      description: "Environmental consulting firm helping businesses achieve sustainability goals",
      category: "Consulting",
      monthlyRevenue: 45000,
      monthlyProfit: 22000,
      tokensForSale: 25000,
      totalSupply: 500000,
      pricePerToken: 1.8,
      tithingPercentage: 20,
      logo: "https://via.placeholder.com/100/10B981/FFFFFF?text=GLC",
      verified: true,
      featured: false,
    },
    {
      id: 3,
      businessName: "FinServe Pro",
      tokenSymbol: "FSP",
      description: "Financial services platform specializing in small business accounting and tax prep",
      category: "Finance",
      monthlyRevenue: 125000,
      monthlyProfit: 68000,
      tokensForSale: 100000,
      totalSupply: 1000000,
      pricePerToken: 3.2,
      tithingPercentage: 20,
      logo: "https://via.placeholder.com/100/F59E0B/FFFFFF?text=FSP",
      verified: true,
      featured: true,
    },
    {
      id: 4,
      businessName: "HealthHub Network",
      tokenSymbol: "HHN",
      description: "Telehealth platform connecting patients with healthcare providers worldwide",
      category: "Healthcare",
      monthlyRevenue: 220000,
      monthlyProfit: 95000,
      tokensForSale: 150000,
      totalSupply: 2000000,
      pricePerToken: 4.5,
      tithingPercentage: 20,
      logo: "https://via.placeholder.com/100/EF4444/FFFFFF?text=HHN",
      verified: true,
      featured: true,
    },
    {
      id: 5,
      businessName: "EduMaster Online",
      tokenSymbol: "EDU",
      description: "Online education platform with courses in technology, business, and creative arts",
      category: "Education",
      monthlyRevenue: 62000,
      monthlyProfit: 31000,
      tokensForSale: 40000,
      totalSupply: 800000,
      pricePerToken: 1.5,
      tithingPercentage: 20,
      logo: "https://via.placeholder.com/100/9333EA/FFFFFF?text=EDU",
      verified: false,
      featured: false,
    },
    {
      id: 6,
      businessName: "BuildRight Construction",
      tokenSymbol: "BRC",
      description: "Commercial construction company specializing in sustainable building practices",
      category: "Construction",
      monthlyRevenue: 340000,
      monthlyProfit: 78000,
      tokensForSale: 80000,
      totalSupply: 1000000,
      pricePerToken: 5.0,
      tithingPercentage: 20,
      logo: "https://via.placeholder.com/100/F97316/FFFFFF?text=BRC",
      verified: true,
      featured: false,
    },
  ];

  const categories = ["all", "Technology", "Consulting", "Finance", "Healthcare", "Education", "Construction"];

  const filteredListings = marketplaceListings.filter((listing) => {
    const matchesSearch =
      listing.businessName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      listing.tokenSymbol.toLowerCase().includes(searchTerm.toLowerCase()) ||
      listing.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = filterCategory === "all" || listing.category === filterCategory;
    return matchesSearch && matchesCategory;
  });

  const handleViewDetails = (listing: any) => {
    setSelectedListing(listing);
    onOpen();
  };

  const calculateMonthlyPayout = (listing: any, tokensOwned: number) => {
    const tithingAmount = listing.monthlyProfit * (listing.tithingPercentage / 100);
    const ownershipPercentage = tokensOwned / listing.totalSupply;
    return (tithingAmount * ownershipPercentage).toFixed(2);
  };

  return (
    <Box bg={bg} minHeight="100vh">
      <NavbarWithCallToAction />

      <Container maxW="1400px" py={16}>
        <VStack spacing={16} align="stretch">
          {/* Hero */}
          <Box textAlign="center">
            <Badge colorScheme="purple" fontSize="md" px={4} py={2} mb={4}>
              Token Marketplace
            </Badge>
            <Heading
              as="h1"
              fontSize={{ base: "4xl", md: "6xl" }}
              fontWeight="800"
              mb={6}
              color={textPrimary}
              bgGradient="linear(to-r, #9333EA, #F59E0B)"
              bgClip="text"
            >
              Invest in Tokenized Businesses
            </Heading>
            <Text fontSize="xl" color={textSecondary} maxW="900px" mx="auto" mb={8}>
              Browse businesses that have tokenized on the Business Builder platform.
              Buy tokens, receive monthly profit distributions, and participate in
              governance.
            </Text>

            {/* Stats */}
            <SimpleGrid columns={{ base: 1, md: 4 }} spacing={6} maxW="1000px" mx="auto" mb={8}>
              {[
                { label: "Active Listings", value: marketplaceListings.length, icon: FaStore, color: "#3B82F6" },
                { label: "Total Volume", value: "$2.4M", icon: FaChartLine, color: "#10B981" },
                { label: "Verified Businesses", value: "5", icon: FaTrophy, color: "#F59E0B" },
                { label: "Avg Monthly ROI", value: "4.2%", icon: FaRocket, color: "#9333EA" },
              ].map((stat, index) => (
                <Box
                  key={index}
                  p={4}
                  bg={`${stat.color}20`}
                  borderRadius="lg"
                  borderWidth="1px"
                  borderColor={`${stat.color}40`}
                  textAlign="center"
                >
                  <Icon as={stat.icon} w={8} h={8} color={stat.color} mb={2} />
                  <Text fontSize="2xl" fontWeight="700" color={textPrimary}>
                    {stat.value}
                  </Text>
                  <Text fontSize="xs" color={textSecondary}>
                    {stat.label}
                  </Text>
                </Box>
              ))}
            </SimpleGrid>
          </Box>

          {/* Search & Filters */}
          <Card
            bg={cardGradientBg}
            backdropFilter="blur(10px)"
            boxShadow="0 8px 32px 0 rgba(0, 0, 0, 0.37)"
            borderRadius="2xl"
            borderWidth="1px"
            borderColor={cardBorder}
          >
            <CardBody p={6}>
              <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
                <InputGroup size="lg">
                  <InputLeftElement>
                    <SearchIcon color={textMuted} />
                  </InputLeftElement>
                  <Input
                    placeholder="Search businesses or tokens..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </InputGroup>
                <Select
                  size="lg"
                  value={filterCategory}
                  onChange={(e) => setFilterCategory(e.target.value)}
                >
                  {categories.map((cat) => (
                    <option key={cat} value={cat}>
                      {cat === "all" ? "All Categories" : cat}
                    </option>
                  ))}
                </Select>
                <Button
                  size="lg"
                  bg="#9333EA"
                  color="white"
                  _hover={{ bg: "#7C3AED" }}
                  onClick={() => navigate("/businessbuilder/list-business")}
                >
                  List Your Business
                </Button>
              </SimpleGrid>
            </CardBody>
          </Card>

          {/* Featured Listings */}
          <Box>
            <Flex align="center" mb={6}>
              <Icon as={FaFire} w={8} h={8} color="#F59E0B" mr={3} />
              <Heading size="xl" color={textPrimary}>
                Featured Listings
              </Heading>
            </Flex>
            <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={6}>
              {filteredListings
                .filter((listing) => listing.featured)
                .map((listing) => (
                  <Card
                    key={listing.id}
                    bg={cardGradientBg}
                    borderWidth="2px"
                    borderColor="#F59E0B"
                    boxShadow="0 8px 24px rgba(245, 158, 11, 0.3)"
                    borderRadius="xl"
                    position="relative"
                    _hover={{
                      transform: "translateY(-4px)",
                      boxShadow: "0 12px 32px rgba(245, 158, 11, 0.4)",
                    }}
                    transition="all 0.3s"
                  >
                    <Badge
                      position="absolute"
                      top="-12px"
                      right="20px"
                      bg="#F59E0B"
                      color="white"
                      px={3}
                      py={1}
                      fontSize="xs"
                    >
                      FEATURED
                    </Badge>
                    <CardBody p={6}>
                      <Flex align="center" mb={4}>
                        <Avatar src={listing.logo} size="lg" mr={4} />
                        <Box flex={1}>
                          <Flex align="center" gap={2}>
                            <Heading size="md" color={textPrimary}>
                              {listing.businessName}
                            </Heading>
                            {listing.verified && (
                              <Icon as={CheckIcon} color="#10B981" boxSize={4} />
                            )}
                          </Flex>
                          <Text fontSize="sm" color={textMuted}>
                            ${listing.tokenSymbol}
                          </Text>
                        </Box>
                      </Flex>

                      <Badge colorScheme="blue" mb={3}>
                        {listing.category}
                      </Badge>

                      <Text fontSize="sm" color={textSecondary} mb={4} noOfLines={2}>
                        {listing.description}
                      </Text>

                      <Divider mb={4} />

                      <SimpleGrid columns={2} spacing={4} mb={4}>
                        <Stat>
                          <StatLabel fontSize="xs" color={textSecondary}>
                            Monthly Profit
                          </StatLabel>
                          <StatNumber fontSize="lg" color="#10B981">
                            ${listing.monthlyProfit.toLocaleString()}
                          </StatNumber>
                        </Stat>
                        <Stat>
                          <StatLabel fontSize="xs" color={textSecondary}>
                            Price/Token
                          </StatLabel>
                          <StatNumber fontSize="lg" color="#9333EA">
                            ${listing.pricePerToken}
                          </StatNumber>
                        </Stat>
                      </SimpleGrid>

                      <Box
                        p={3}
                        bg="rgba(147, 51, 234, 0.1)"
                        borderRadius="md"
                        borderWidth="1px"
                        borderColor="rgba(147, 51, 234, 0.3)"
                        mb={4}
                      >
                        <Text fontSize="xs" color={textSecondary} mb={1}>
                          {listing.tokensForSale.toLocaleString()} tokens available
                        </Text>
                        <Text fontSize="xs" color={textMuted}>
                          20% profit tithing to token holders
                        </Text>
                      </Box>

                      <Button
                        width="100%"
                        bg="#9333EA"
                        color="white"
                        _hover={{ bg: "#7C3AED" }}
                        onClick={() => handleViewDetails(listing)}
                      >
                        View Details
                      </Button>
                    </CardBody>
                  </Card>
                ))}
            </SimpleGrid>
          </Box>

          {/* All Listings */}
          <Box>
            <Heading size="xl" mb={6} color={textPrimary}>
              All Listings
            </Heading>
            <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={6}>
              {filteredListings
                .filter((listing) => !listing.featured)
                .map((listing) => (
                  <Card
                    key={listing.id}
                    bg={cardGradientBg}
                    borderWidth="1px"
                    borderColor={cardBorder}
                    boxShadow="0 4px 16px rgba(0, 0, 0, 0.2)"
                    borderRadius="xl"
                    _hover={{
                      transform: "translateY(-4px)",
                      boxShadow: "0 8px 24px rgba(0, 0, 0, 0.3)",
                    }}
                    transition="all 0.3s"
                  >
                    <CardBody p={6}>
                      <Flex align="center" mb={4}>
                        <Avatar src={listing.logo} size="lg" mr={4} />
                        <Box flex={1}>
                          <Flex align="center" gap={2}>
                            <Heading size="md" color={textPrimary}>
                              {listing.businessName}
                            </Heading>
                            {listing.verified && (
                              <Icon as={CheckIcon} color="#10B981" boxSize={4} />
                            )}
                          </Flex>
                          <Text fontSize="sm" color={textMuted}>
                            ${listing.tokenSymbol}
                          </Text>
                        </Box>
                      </Flex>

                      <Badge colorScheme="blue" mb={3}>
                        {listing.category}
                      </Badge>

                      <Text fontSize="sm" color={textSecondary} mb={4} noOfLines={2}>
                        {listing.description}
                      </Text>

                      <Divider mb={4} />

                      <SimpleGrid columns={2} spacing={4} mb={4}>
                        <Stat>
                          <StatLabel fontSize="xs" color={textSecondary}>
                            Monthly Profit
                          </StatLabel>
                          <StatNumber fontSize="lg" color="#10B981">
                            ${listing.monthlyProfit.toLocaleString()}
                          </StatNumber>
                        </Stat>
                        <Stat>
                          <StatLabel fontSize="xs" color={textSecondary}>
                            Price/Token
                          </StatLabel>
                          <StatNumber fontSize="lg" color="#9333EA">
                            ${listing.pricePerToken}
                          </StatNumber>
                        </Stat>
                      </SimpleGrid>

                      <Box
                        p={3}
                        bg="rgba(147, 51, 234, 0.1)"
                        borderRadius="md"
                        borderWidth="1px"
                        borderColor="rgba(147, 51, 234, 0.3)"
                        mb={4}
                      >
                        <Text fontSize="xs" color={textSecondary} mb={1}>
                          {listing.tokensForSale.toLocaleString()} tokens available
                        </Text>
                        <Text fontSize="xs" color={textMuted}>
                          20% profit tithing to token holders
                        </Text>
                      </Box>

                      <Button
                        width="100%"
                        bg="#3B82F6"
                        color="white"
                        _hover={{ bg: "#2563EB" }}
                        onClick={() => handleViewDetails(listing)}
                      >
                        View Details
                      </Button>
                    </CardBody>
                  </Card>
                ))}
            </SimpleGrid>
          </Box>

          {/* List Your Business CTA */}
          <Box
            textAlign="center"
            bg="linear-gradient(135deg, #9333EA 0%, #7C3AED 50%, #6366F1 100%)"
            p={12}
            borderRadius="2xl"
            color="white"
          >
            <Icon as={FaBuilding} w={12} h={12} mb={4} />
            <Heading size="2xl" mb={4}>
              Tokenize Your Business
            </Heading>
            <Text fontSize="xl" mb={8} maxW="700px" mx="auto">
              Join the Business Builder platform and offer tokens to investors.
              Raise capital, share profits, and build a community of supporters.
            </Text>
            <Button
              size="lg"
              bg="white"
              color="#9333EA"
              rightIcon={<ChevronRightIcon />}
              _hover={{
                transform: "translateY(-2px)",
                boxShadow: "0 8px 24px rgba(255, 255, 255, 0.3)",
              }}
              px={10}
              py={7}
              fontSize="xl"
              onClick={() => navigate("/businessbuilder/list-business")}
            >
              List Your Business
            </Button>
          </Box>
        </VStack>
      </Container>

      {/* Details Modal */}
      <Modal isOpen={isOpen} onClose={onClose} size="3xl">
        <ModalOverlay />
        <ModalContent bg={cardGradientBg}>
          <ModalHeader>
            {selectedListing && (
              <Flex align="center">
                <Avatar src={selectedListing.logo} size="lg" mr={4} />
                <Box>
                  <Flex align="center" gap={2}>
                    <Heading size="lg" color={textPrimary}>
                      {selectedListing.businessName}
                    </Heading>
                    {selectedListing.verified && (
                      <Icon as={CheckIcon} color="#10B981" />
                    )}
                  </Flex>
                  <Text fontSize="md" color={textMuted}>
                    ${selectedListing.tokenSymbol}
                  </Text>
                </Box>
              </Flex>
            )}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {selectedListing && (
              <VStack spacing={6} align="stretch">
                <Box>
                  <Badge colorScheme="blue" mb={2}>
                    {selectedListing.category}
                  </Badge>
                  <Text fontSize="md" color={textSecondary}>
                    {selectedListing.description}
                  </Text>
                </Box>

                <Divider />

                <SimpleGrid columns={2} spacing={6}>
                  <Stat>
                    <StatLabel>Monthly Revenue</StatLabel>
                    <StatNumber color="#3B82F6">
                      ${selectedListing.monthlyRevenue.toLocaleString()}
                    </StatNumber>
                  </Stat>
                  <Stat>
                    <StatLabel>Monthly Profit</StatLabel>
                    <StatNumber color="#10B981">
                      ${selectedListing.monthlyProfit.toLocaleString()}
                    </StatNumber>
                  </Stat>
                  <Stat>
                    <StatLabel>Price per Token</StatLabel>
                    <StatNumber color="#9333EA">
                      ${selectedListing.pricePerToken}
                    </StatNumber>
                  </Stat>
                  <Stat>
                    <StatLabel>Tokens Available</StatLabel>
                    <StatNumber color="#F59E0B">
                      {selectedListing.tokensForSale.toLocaleString()}
                    </StatNumber>
                  </Stat>
                </SimpleGrid>

                <Box
                  p={4}
                  bg="rgba(147, 51, 234, 0.1)"
                  borderRadius="lg"
                  borderWidth="1px"
                  borderColor="rgba(147, 51, 234, 0.3)"
                >
                  <Heading size="sm" mb={3} color={textPrimary}>
                    Profit Tithing Model
                  </Heading>
                  <Text fontSize="sm" color={textSecondary} mb={2}>
                    This business distributes <strong>20% of monthly profit</strong> to
                    token holders proportionally.
                  </Text>
                  <Text fontSize="sm" color={textSecondary}>
                    Example: If you own 10,000 tokens ({((10000 / selectedListing.totalSupply) * 100).toFixed(2)}% ownership),
                    you would receive <strong>${calculateMonthlyPayout(selectedListing, 10000)}/month</strong> based
                    on current profit levels.
                  </Text>
                </Box>
              </VStack>
            )}
          </ModalBody>

          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onClose}>
              Close
            </Button>
            <Button
              bg="#9333EA"
              color="white"
              _hover={{ bg: "#7C3AED" }}
              rightIcon={<FaShoppingCart />}
              onClick={() => {
                navigate("/businessbuilder/tithing-calculator", {
                  state: { business: selectedListing },
                });
              }}
            >
              Calculate Returns
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <FooterWithFourColumns />
    </Box>
  );
};

export default Marketplace;
